/**
 * Jornada de trabalho da POC — agora DB-driven: as janelas de turno vêm do backend
 * (`GET /Production/shifts`, que lê `masterdata.shift` da planta) via `useWorkSchedule`.
 * Este módulo é puro: opera sobre um `WorkScheduleConfig` (lista de janelas + fuso) passado
 * pelos consumidores. `DEFAULT_SCHEDULE` é o fallback (uma janela 08:00–17:00, UTC−3) usado
 * até o backend responder ou quando a planta não tem turno cabeado.
 *
 * Regra (espelho do `ShiftAttributionService.Resolve`): o instante, convertido para o minuto-do-dia
 * local (UTC + offset), está DENTRO do turno se cai em QUALQUER janela; caso contrário, fora.
 * Janela com `endMinute <= startMinute` cruza a meia-noite.
 */

export interface ShiftWindow {
  /** Minuto-do-dia local do início (0..1439). */
  startMinute: number;
  /** Minuto-do-dia local do fim (0..1439). <= start ⇒ cruza a meia-noite. */
  endMinute: number;
}

export interface WorkScheduleConfig {
  windows: ShiftWindow[];
  /** Offset fixo do fuso da POC (ex.: −3). */
  tzOffsetHours: number;
}

/** Fallback: um turno 08:00–17:00, UTC−3 (até o backend responder / planta sem turno). */
export const DEFAULT_SCHEDULE: WorkScheduleConfig = {
  windows: [{ startMinute: 8 * 60, endMinute: 17 * 60 }],
  tzOffsetHours: -3,
};

/** Estado sintético do segmento fora de turno (sem produção) na linha do tempo. */
export const OFF_SHIFT_STATE = "ForaDeTurno";

/** Estado sintético para PRODUÇÃO fora de turno (produzindo fora do turno) — destacado. */
export const OFF_SHIFT_PROD_STATE = "ProducaoForaTurno";

/** Cor de "produção fora de turno" — violeta forte, não usada por nenhum outro estado. */
export const OFF_SHIFT_PROD_COLOR = "#7C3AED";

/** Estados considerados "produção" (estação produzindo um ciclo). */
export function isProducingState(state: string): boolean {
  return state === "Produzindo" || state === "CicloExcedido";
}

const MS_PER_HOUR = 3_600_000;

/** Minuto-do-dia local (0..1439) de um instante UTC, no fuso da POC (browser-tz-independente). */
export function localMinuteOfDay(instant: Date, tzOffsetHours: number): number {
  const shifted = new Date(instant.getTime() + tzOffsetHours * MS_PER_HOUR);
  return shifted.getUTCHours() * 60 + shifted.getUTCMinutes();
}

/** Hora local (0..23) de um instante UTC, no fuso da POC. */
export function localHour(instant: Date, tzOffsetHours: number): number {
  return new Date(instant.getTime() + tzOffsetHours * MS_PER_HOUR).getUTCHours();
}

/** Segmentos [start,end) em [0,1440) de uma janela (normaliza cruza-meia-noite). */
function windowSegments(w: ShiftWindow): Array<[number, number]> {
  if (w.startMinute < w.endMinute) return [[w.startMinute, w.endMinute]];
  const segs: Array<[number, number]> = [];
  if (w.startMinute < 1440) segs.push([w.startMinute, 1440]);
  if (w.endMinute > 0) segs.push([0, w.endMinute]);
  return segs;
}

/** O minuto-do-dia cai dentro de ALGUMA janela do turno? */
export function isInShiftMinute(minuteOfDay: number, config: WorkScheduleConfig): boolean {
  return config.windows.some((w) =>
    windowSegments(w).some(([s, e]) => minuteOfDay >= s && minuteOfDay < e),
  );
}

/** true se o instante cai DENTRO do turno (união das janelas). */
export function isInShift(instant: Date, config: WorkScheduleConfig): boolean {
  return isInShiftMinute(localMinuteOfDay(instant, config.tzOffsetHours), config);
}

/** true se o instante cai FORA do turno (complemento da união das janelas). */
export function isOffShift(instant: Date, config: WorkScheduleConfig): boolean {
  return !isInShift(instant, config);
}

/** A HORA local (0..23) está totalmente fora do turno? (nenhuma janela cobre nenhum minuto dela) */
export function isOffShiftHour(hour: number, config: WorkScheduleConfig): boolean {
  const lo = hour * 60;
  const hi = lo + 60;
  const covered = config.windows.some((w) =>
    windowSegments(w).some(([s, e]) => Math.max(s, lo) < Math.min(e, hi)),
  );
  return !covered;
}

/** Próxima fronteira (início/fim de qualquer janela) estritamente após `ms`, em tempo local. */
function nextBoundaryMs(ms: number, config: WorkScheduleConfig): number {
  const offsetMs = config.tzOffsetHours * MS_PER_HOUR;
  const localMs = ms + offsetMs;
  const d = new Date(localMs);
  const curSec =
    d.getUTCHours() * 3600 + d.getUTCMinutes() * 60 + d.getUTCSeconds() + d.getUTCMilliseconds() / 1000;

  const edges = new Set<number>();
  for (const w of config.windows) {
    edges.add(w.startMinute * 60);
    edges.add(w.endMinute * 60);
  }

  let best = Infinity;
  edges.forEach((eSec) => {
    const cand = eSec > curSec ? eSec : eSec + 86400; // dobra pro dia seguinte se já passou
    if (cand < best) best = cand;
  });
  if (!Number.isFinite(best)) best = curSec + 86400; // sem janelas (não ocorre com fallback)

  const boundaryLocalMs = localMs + (best - curSec) * 1000;
  return boundaryLocalMs - offsetMs;
}

export interface ShiftSlice {
  /** Início da fatia (ISO, UTC). */
  startedAt: string;
  seconds: number;
  offShift: boolean;
}

/**
 * Divide `[startIso, startIso + seconds)` nas fronteiras das janelas do turno, marcando cada
 * fatia como dentro/fora. Entre duas fronteiras consecutivas o status (união das janelas) é
 * constante, então o corte por fronteira é exato. Preserva contiguidade e soma de segundos.
 */
export function splitIntervalByShift(startIso: string, seconds: number, config: WorkScheduleConfig): ShiftSlice[] {
  const out: ShiftSlice[] = [];
  const startMs = new Date(startIso).getTime();
  const endMs = startMs + seconds * 1000;
  let curMs = startMs;
  let guard = 0;
  while (curMs < endMs && guard++ < 100000) {
    const off = isOffShift(new Date(curMs), config);
    const sliceEnd = Math.min(nextBoundaryMs(curMs, config), endMs);
    const sliceSeconds = (sliceEnd - curMs) / 1000;
    if (sliceSeconds > 0) {
      out.push({ startedAt: new Date(curMs).toISOString(), seconds: sliceSeconds, offShift: off });
    }
    if (sliceEnd <= curMs) break; // guarda extra contra estagnação
    curMs = sliceEnd;
  }
  return out;
}

/**
 * Formata um instante (ISO UTC) como HH:mm no fuso FIXO da POC, independente do fuso do browser,
 * para os rótulos baterem com as fronteiras do turno e a banda "fora de turno".
 */
export function formatLocalTime(iso: string, locale: string, tzOffsetHours: number): string {
  const shifted = new Date(new Date(iso).getTime() + tzOffsetHours * MS_PER_HOUR);
  return shifted.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", timeZone: "UTC" });
}

/** Rótulo das janelas do turno, ex.: "07:00–16:48, 16:30–23:59". */
export function shiftWindowLabel(config: WorkScheduleConfig): string {
  const fmt = (min: number): string => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };
  return config.windows.map((w) => `${fmt(w.startMinute)}–${fmt(w.endMinute)}`).join(", ");
}
